import { BIOME_REGISTRY } from '../data/biomes.config';
import { DungeonInitialConfig, DungeonRunConfig } from '../types/dungeon.types';
import { ResolvedFloor } from '../types/floor.types';

/** Monster level is rolled in `[average - SPREAD, average + SPREAD]`. */
const MONSTER_LEVEL_SPREAD = 3;

/**
 * Clones a dungeon's static `initialConfig` into a mutable, run-scoped
 * `DungeonRunConfig` tagged with its `dungeonId`.
 */
export function cloneDungeonRunConfig(
  dungeonId: string,
  initial: DungeonInitialConfig,
): DungeonRunConfig {
  return { dungeonId, ...structuredClone(initial) };
}

/**
 * Resolves the effective config of a floor from a run config: biome band
 * lookup + optional `CustomFloor` override.
 */
export function resolveFloor(
  cfg: DungeonRunConfig,
  floor: number,
): ResolvedFloor {
  const band =
    cfg.biomeBands.find(
      (b) => floor >= b.floorRange[0] && floor <= b.floorRange[1],
    ) ?? cfg.biomeBands[0];
  const custom = cfg.customFloors?.find((c) => c.floor === floor);
  const biomeId = custom?.biomeId ?? band.biomeId;
  const biome = BIOME_REGISTRY[biomeId] ?? BIOME_REGISTRY[band.biomeId];
  return {
    floor,
    biome,
    floorType: custom?.floorType ?? 'normal',
    width: custom?.width ?? biome.width,
    height: custom?.height ?? biome.height,
    style: custom?.style ?? biome.style,
    tileset: custom?.tileset ?? biome.tileset,
    roomCount: biome.roomCount,
    averageMonsterLevel: cfg.averageMonsterLevel,
    bossMonsterId: custom?.bossMonsterId,
    bossLevel: custom?.bossLevel,
  };
}

/** Rolls a monster level around the dungeon average (`average ± 3`, min 1). */
export function rollMonsterLevel(average: number): number {
  const offset =
    Math.floor(Math.random() * (MONSTER_LEVEL_SPREAD * 2 + 1)) -
    MONSTER_LEVEL_SPREAD;
  return Math.max(1, average + offset);
}
